import { createAsyncThunk } from '@reduxjs/toolkit';
import { DeliveryWarehousesAPI, RegionsAPI } from '@/api';
import { formatApiError } from '@/utils';
import { IApiError } from '@/models/apiError.model';
import {
  IGetRegionsDTO,
  IGetRegionSettlementsDTO,
  IGetPickupDTO,
  IGetRegionSettlementsResponse,
  IGetPickupsResponse,
  IGetRegionsResponse,
  IAutocompleteRegionsDTO,
  IAutocompleteRegionsResponse,
  IAutocompleteSettlementsDTO,
  IAutocompleteSettlementsResponse,
  IGetPickupResponse,
  ICreatePickupDTO,
  IEditPickupDTO,
} from '@/models/regions.model';
import { REGIONS_SLICE_NAME } from './models';

export const getRegions = createAsyncThunk<IGetRegionsResponse, IGetRegionsDTO, { serializedErrorType: IApiError }>(
  `${REGIONS_SLICE_NAME}/getRegions`,
  async (data) => {
    const response = await RegionsAPI.getRegions(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getPickupLocations = createAsyncThunk<
  IGetPickupsResponse,
  IGetPickupDTO,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/getPickupLocations`,
  async (data) => {
    const response = await DeliveryWarehousesAPI.getDeliveryWarehouses(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getRegionSettlements = createAsyncThunk<
  IGetRegionSettlementsResponse,
  IGetRegionSettlementsDTO,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/getRegionSettlements`,
  async (data) => {
    const response = await RegionsAPI.getRegionSettlements(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const settlementToggleActive = createAsyncThunk<
  IGetRegionSettlementsResponse,
  string,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/settlementToggleActive`,
  async (id) => {
    const response = await RegionsAPI.settlementToggleActive(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const settlementToggleHeader = createAsyncThunk<
  IGetRegionSettlementsResponse,
  string,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/settlementToggleHeader`,
  async (id) => {
    const response = await RegionsAPI.settlementToggleHeader(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const regionToggleActive = createAsyncThunk<IGetRegionsResponse, string, { serializedErrorType: IApiError }>(
  `${REGIONS_SLICE_NAME}/regionToggleActive`,
  async (id) => {
    const response = await RegionsAPI.regionToggleActive(id);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const autoCompleteRegions = createAsyncThunk<
  IAutocompleteRegionsResponse,
  IAutocompleteRegionsDTO,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/autoCompleteRegions`,
  async (data) => {
    const response = await RegionsAPI.regionsAutoComplete(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const autoCompleteSettlements = createAsyncThunk<
  IAutocompleteSettlementsResponse,
  IAutocompleteSettlementsDTO,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/autoCompleteSettlements`,
  async (data) => {
    const response = await RegionsAPI.settlementsAutoComplete(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const createDeliveryWarehouse = createAsyncThunk<
  IGetPickupResponse,
  ICreatePickupDTO,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/createDeliveryWarehouse`,
  async (data) => {
    const response = await DeliveryWarehousesAPI.createDeliveryWarehouse(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateDeliveryWarehouse = createAsyncThunk<
  IGetPickupResponse,
  IEditPickupDTO,
  { serializedErrorType: IApiError }
>(
  `${REGIONS_SLICE_NAME}/updateDeliveryWarehouse`,
  async (data) => {
    const response = await DeliveryWarehousesAPI.updateDeliveryWarehouse(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteDeliveryWarehouse = createAsyncThunk<IGetPickupResponse, string, { serializedErrorType: IApiError }>(
  `${REGIONS_SLICE_NAME}/deleteDeliveryWarehouse`,
  async (data) => {
    const response = await DeliveryWarehousesAPI.deleteDeliveryWarehouse(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getDeliveryWarehouse = createAsyncThunk<IGetPickupResponse, string, { serializedErrorType: IApiError }>(
  `${REGIONS_SLICE_NAME}/getDeliveryWarehouse`,
  async (data) => {
    const response = await DeliveryWarehousesAPI.getDeliveryWarehouse(data);
    return response.data;
  },
  { serializeError: formatApiError },
);
